import type { FC } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import { GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY } from '@/lib/const';
import * as CM from '@/messages/common';
import * as PM from '@/messages/payment';

export type ReturnSubmitFooterProps = {
  isGuestMode: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onRecaptchaChange: (token: string | null) => void;
  onRecaptchaExpired: () => void;
  onSubmit: () => void;
  submitErrorMessage: string | null;
  submitRecaptchaKey: number;
};

export const ReturnSubmitFooter: FC<ReturnSubmitFooterProps> = ({
  isGuestMode,
  isSubmitDisabled,
  isSubmitting,
  onCancel,
  onRecaptchaChange,
  onRecaptchaExpired,
  onSubmit,
  submitErrorMessage,
  submitRecaptchaKey,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      {submitErrorMessage ? (
        <Box aria-live='assertive' marginTop={2} role='alert'>
          <Typography color='primary' scale='p3'>
            {submitErrorMessage}
          </Typography>
        </Box>
      ) : null}

      <Flex
        alignItems='center'
        flexDirection='column'
        gridGap={2}
        marginTop={1}
      >
        {isGuestMode ? (
          <Box>
            <ReCAPTCHA
              key={submitRecaptchaKey}
              onChange={onRecaptchaChange}
              onExpired={onRecaptchaExpired}
              sitekey={GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY}
            />
          </Box>
        ) : null}
        <Flex flexWrap='wrap' gridGap={2} justifyContent='center'>
          <Button
            data-test-id='OrderReturnViewCancelButton'
            disabled={isSubmitting}
            onClick={onCancel}
            variant='outlined'
          >
            {formatMessage(CM.commonMessages.back)}
          </Button>
          <Button
            data-test-id='OrderReturnViewContinueButton'
            disabled={isSubmitDisabled}
            isProcessing={isSubmitting}
            onClick={onSubmit}
            type='button'
          >
            {formatMessage(PM.paymentMessages.initiateReturnOrder)}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};
